import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Search, MessageSquare, AlertTriangle } from "lucide-react";
import { getQueueLogs, getQueueLogsPortal } from "../../api/fees/feeApi";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Tabs,
  Badge,
  Table,
  Pagination,
  Alert,
  EmptyState,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const STATUS_TABS = ["ALL", "QUEUED", "SENT", "FAILED", "SKIPPED"];

const BADGE_VARIANTS = {
  QUEUED: "warning",
  SENT: "success",
  FAILED: "danger",
  SKIPPED: "default",
};

const WhatsAppQueueDashboard = ({ isPortal = false }) => {
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({ QUEUED: 0, SENT: 0, FAILED: 0, SKIPPED: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const debounceRef = useRef(null);

  const fetchLogs = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = { page, limit: 50 };
        if (status !== "ALL") params.status = status;
        if (search.trim()) params.search = search.trim();

        const res = isPortal ? await getQueueLogsPortal(params) : await getQueueLogs(params);
        setLogs(res.data || []);
        setCounts(res.counts || { QUEUED: 0, SENT: 0, FAILED: 0, SKIPPED: 0 });
        setPagination(res.pagination || { page: 1, totalPages: 1, total: 0 });
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load message queue.");
      } finally {
        setLoading(false);
      }
    },
    [status, search, isPortal]
  );

  // Refetch when status filter changes (immediate)
  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Debounced refetch on search typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchLogs(1), 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Live polling every 10s
  useEffect(() => {
    const interval = setInterval(() => fetchLogs(pagination.page), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, status, search]);

  const totalAll = counts.QUEUED + counts.SENT + counts.FAILED + counts.SKIPPED;

  const tabOptions = useMemo(
    () =>
      STATUS_TABS.map((tab) => {
        const count = tab === "ALL" ? totalAll : counts[tab] || 0;
        return {
          value: tab,
          label: `${tab} (${count})`,
        };
      }),
    [counts, totalAll]
  );

  const columns = [
    {
      key: "recipientPhone",
      header: "Phone",
      render: (log) => (
        <span className="font-mono text-xs font-medium text-neutral-900 dark:text-neutral-100">
          +{log.recipientPhone}
        </span>
      ),
    },
    {
      key: "channel",
      header: "Channel",
      render: (log) => (
        <Badge variant="primary" className="text-[11px]">
          {log.channel}
        </Badge>
      ),
    },
    {
      key: "eventType",
      header: "Event",
      render: (log) => (
        <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
          {log.eventType}
        </span>
      ),
    },
    {
      key: "message",
      header: "Message",
      render: (log) => (
        <p className="max-w-xs truncate text-xs text-neutral-600 dark:text-neutral-300" title={log.message}>
          {log.message}
        </p>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (log) => (
        <div className="space-y-1">
          <Badge variant={BADGE_VARIANTS[log.status] || "default"}>
            {log.status}
          </Badge>
          {log.status === "FAILED" && log.errorReason && (
            <p
              className="flex items-center gap-1 text-[11px] font-medium text-danger-500 max-w-xs truncate"
              title={log.errorReason}
            >
              <AlertTriangle className="h-3 w-3 shrink-0" />
              <span>{log.errorReason}</span>
            </p>
          )}
        </div>
      ),
    },
    {
      key: "createdAt",
      header: "Queued At",
      nowrap: true,
      render: (log) => (
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          {log.createdAt ? new Date(log.createdAt).toLocaleString("en-IN") : "—"}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b bg-neutral-50/50">
        <div>
          <CardTitle>Message Queue</CardTitle>
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
            Real-time status tracking for automated WhatsApp & SMS dispatches.
          </p>
        </div>

        <div className="w-full sm:w-64">
          <Input
            placeholder="Search phone or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="border-b px-4 pt-2 bg-white dark:bg-neutral-900 overflow-x-auto">
          <Tabs
            tabs={tabOptions}
            value={status}
            onChange={(val) => setStatus(val)}
          />
        </div>

        

        <Table
          columns={columns}
          data={logs}
          isLoading={loading}
          emptyState={
            <EmptyState
              icon={MessageSquare}
              title="No queue logs found"
              description={
                search
                  ? "Try adjusting your search criteria or changing status filters."
                  : "Outbound messages will appear here as soon as they are queued."
              }
            />
          }
        />

        {pagination.totalPages > 1 && (
          <div className="border-t p-4 bg-neutral-50/50 flex items-center justify-between">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={(page) => fetchLogs(page)}
            />
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Total logs: <strong className="text-neutral-900 dark:text-neutral-100">{pagination.total}</strong>
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WhatsAppQueueDashboard;